import React, { useCallback, useId, useState } from 'react';
import useDisclosure from '@/hooks/useDisclosure';
import Drawer from '@/components/organisms/drawer';
import { useTranslations } from 'use-intl';
import InfoBanner from '@/components/molecules/info-banner';
import QuickOrderContent from '../quick-order-accordion/quick-order-content';
import { QuickOrderDesktopProps } from '../types';
import { QuickOrderDesktopProvider } from './quick-order-csv-upload/context';

const QuickOrderDesktop = ({
  downloadLink,
  csvProducts,
  csvProductsLoading,
  addItem,
  addItemDisabled,
  handleSKUsUpdate,
  onSkuLookup,
  initialItems,
  isOpen: isOpenProp,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  backOrdersAllowed,
  paymentTerms,
}: QuickOrderDesktopProps) => {
  const translate = useTranslations();
  const disclosure = useDisclosure();
  const isOpen = isOpenProp ?? disclosure.isOpen;
  const onOpen = onOpenProp ?? disclosure.onOpen;
  const onClose = onCloseProp ?? disclosure.onClose;
  const drawerId = useId();

  // EWM-931 — the entered rows live inside QuickOrderContent, but the close
  // paths (close control, scrim, Escape) are owned by the Drawer, so the content
  // reports its dirty state up here. Discarding remounts the content via
  // `contentKey`, which clears every row, the quantities and any partially
  // typed SKU in one go — no reset API to keep in sync with the content's state.
  const [isDirty, setIsDirty] = useState(false);
  const [contentKey, setContentKey] = useState(0);
  const handleDiscard = useCallback(() => {
    setIsDirty(false);
    setContentKey((k) => k + 1);
    onClose();
  }, [onClose]);

  return (
    <QuickOrderDesktopProvider
      csvProducts={csvProducts}
      csvProductsLoading={csvProductsLoading}
      downloadLink={downloadLink}
      addItem={addItem}
      handleSKUsUpdate={handleSKUsUpdate}
      onClose={onClose}
      backOrdersAllowed={backOrdersAllowed}
      paymentTerms={paymentTerms}
    >
      {onOpenProp === undefined && (
        <button
          type="button"
          className="hover:text-primary p-1 whitespace-nowrap text-gray-700"
          onClick={onOpen}
          aria-haspopup="dialog"
          aria-controls={drawerId}
          aria-expanded={isOpen}
        >
          {translate('quick-order.quick-order')}
        </button>
      )}
      <Drawer
        id={drawerId}
        direction="right"
        isOpen={isOpen}
        headline={translate('quick-order.drawer-title')}
        subHeadline={translate('quick-order.drawer-subtitle')}
        onClose={onClose}
        confirmOnDirty
        confirmRequiresChoice
        isDirty={isDirty}
        onDiscard={handleDiscard}
        className="!w-screen !max-w-lg"
      >
        {addItemDisabled && (
          <div className="px-6 pt-4">
            <InfoBanner>
              <b>{translate('common.view-only')}</b> {translate('cart.view-only-desc')}
            </InfoBanner>
          </div>
        )}
        <QuickOrderContent
          key={contentKey}
          addItem={addItem}
          addItemDisabled={addItemDisabled}
          closeMenu={onClose}
          onSkuLookup={onSkuLookup}
          initialItems={initialItems}
          onDirtyChange={setIsDirty}
        />
      </Drawer>
    </QuickOrderDesktopProvider>
  );
};
export default QuickOrderDesktop;
